import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Send, Trash2, Lightbulb, Sparkles, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssistantChat({ fullscreen = false }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(fullscreen);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggTitle, setSuggTitle] = useState('');
  const [suggDesc, setSuggDesc] = useState('');
  const [suggSending, setSuggSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load history on open
  useEffect(() => {
    if ((open || fullscreen) && token) {
      axios.get(`${API}/assistant/history`, { headers })
        .then(r => {
          setMessages(r.data.map(m => ({ role: m.role, content: m.content })));
        })
        .catch(() => {});
    }
  }, [open, fullscreen, token]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, { message: text }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Desolé, une erreur s'est produite. Réessayez." }]);
    }
    setLoading(false);
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/assistant/history`, { headers });
      setMessages([]);
    } catch {}
  };

  const submitSuggestion = async () => {
    if (!suggTitle.trim()) return;
    setSuggSending(true);
    try {
      await axios.post(`${API}/assistant/suggestions`, {
        title: suggTitle, description: suggDesc, category: 'user_suggestion'
      }, { headers });
      setShowSuggestion(false);
      setSuggTitle('');
      setSuggDesc('');
      setMessages(prev => [...prev, { role: 'assistant', content: "Merci pour votre suggestion ! Elle a été transmise à notre équipe." }]);
    } catch {}
    setSuggSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating bubble (not in fullscreen mode)
  if (!fullscreen && !open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#D4A853] to-[#B8912F] text-[#0C0C0C] flex items-center justify-center shadow-lg shadow-[#D4A853]/20 hover:scale-110 transition-transform"
        data-testid="assistant-bubble">
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  const containerClass = fullscreen
    ? "flex flex-col h-full bg-[#0C0C0C] rounded-2xl border border-[#1A1A1A]"
    : "fixed bottom-5 right-5 z-50 w-[380px] h-[560px] bg-[#0C0C0C] rounded-2xl border border-[#1A1A1A] shadow-2xl flex flex-col overflow-hidden";

  return (
    <div className={containerClass} data-testid="assistant-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1A1A] bg-[#111] rounded-t-2xl flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4A853] to-[#B8912F] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#0C0C0C]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Assistant HIBISAAS</p>
            <p className="text-[10px] text-[#666]">IA - Configuration & aide</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setShowSuggestion(!showSuggestion)} title="Envoyer une suggestion"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[#888] hover:text-[#D4A853] hover:bg-white/5 transition-colors"
            data-testid="assistant-suggest-btn">
            <Lightbulb className="w-4 h-4" />
          </button>
          <button onClick={clearHistory} title="Effacer l'historique"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[#888] hover:text-red-400 hover:bg-white/5 transition-colors"
            data-testid="assistant-clear-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {!fullscreen && (
            <button onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-[#888] hover:text-white hover:bg-white/5 transition-colors"
              data-testid="assistant-close-btn">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Suggestion form */}
      {showSuggestion && (
        <div className="px-4 py-3 border-b border-[#1A1A1A] bg-[#0D0D0D] flex-shrink-0 space-y-2" data-testid="suggestion-form">
          <p className="text-xs font-medium text-[#D4A853]">Proposer une amelioration</p>
          <input value={suggTitle} onChange={e => setSuggTitle(e.target.value)}
            placeholder="Titre de la suggestion"
            className="w-full h-8 px-3 bg-[#1A1A1A] border border-[#333] rounded-lg text-xs text-white placeholder:text-[#555] focus:outline-none focus:ring-1 focus:ring-[#D4A853]/40"
            data-testid="suggestion-title" />
          <textarea value={suggDesc} onChange={e => setSuggDesc(e.target.value)}
            placeholder="Decrivez votre idee..."
            rows={2}
            className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-lg text-xs text-white placeholder:text-[#555] resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A853]/40"
            data-testid="suggestion-desc" />
          <button onClick={submitSuggestion} disabled={suggSending || !suggTitle.trim()}
            className="w-full h-8 bg-[#D4A853] hover:bg-[#C49A48] text-[#0C0C0C] font-semibold text-xs rounded-lg disabled:opacity-50 transition-colors"
            data-testid="suggestion-submit">
            {suggSending ? 'Envoi...' : 'Envoyer la suggestion'}
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 rounded-2xl bg-[#D4A853]/10 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-[#D4A853]" />
            </div>
            <p className="text-sm font-medium text-white mb-1">Bienvenue !</p>
            <p className="text-xs text-[#888] leading-relaxed">
              Je suis votre assistant HIBISAAS. Je peux vous aider a configurer votre restaurant, repondre a vos questions et prendre vos suggestions.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {["Comment configurer mon menu ?", "Ajouter des tables", "Comment fonctionne le QR ?"].map(q => (
                <button key={q} onClick={() => { setInput(q); }}
                  className="px-3 py-1.5 rounded-full bg-white/5 border border-[#333] text-[10px] text-[#AAA] hover:text-white hover:border-[#D4A853]/40 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
              msg.role === 'user'
                ? 'bg-[#D4A853] text-[#0C0C0C] rounded-br-md'
                : 'bg-[#1A1A1A] text-[#DDD] border border-[#2A2A2A] rounded-bl-md'
            }`}>
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#D4A853] animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#D4A853] animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#D4A853] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-[#1A1A1A] flex-shrink-0 bg-[#0D0D0D] rounded-b-2xl">
        <div className="flex gap-2 items-end">
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ecrivez votre message..."
            rows={1}
            className="flex-1 px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-xl text-sm text-white placeholder:text-[#555] resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A853]/40"
            style={{ maxHeight: '80px' }}
            data-testid="assistant-input" />
          <button onClick={sendMessage} disabled={!input.trim() || loading}
            className="w-9 h-9 rounded-xl bg-[#D4A853] hover:bg-[#C49A48] text-[#0C0C0C] flex items-center justify-center flex-shrink-0 disabled:opacity-30 transition-colors"
            data-testid="assistant-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
